import { AfterViewInit, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { I18nService, StaticService } from '@app/i18n/services';

import { isITerm, TermState, TermType } from '../../../../../../services/legals/legals.models';
import { ILegalDisplay } from './legal-display.models';

@Component({
	selector: 'app-legal-display',
	templateUrl: './legal-display.dialog.html',
	styleUrls: ['./legal-display.dialog.scss']
})
export class LegalDisplayDialogComponent implements OnInit, OnDestroy, AfterViewInit {
	public safeData: SafeUrl;
	public termName: string;
	public languageName: string;
	public stateBadge: string;
	public stateLabel: string;
	private locales: any[] = [];

	constructor(
		@Inject(MAT_DIALOG_DATA) public dialogData: ILegalDisplay,
		private dialogRef: MatDialogRef<LegalDisplayDialogComponent>,
		private sanitizer: DomSanitizer,
		private staticService: StaticService,
		private i18nService: I18nService
	) {}

	public ngOnInit(): void {
		this.safeData = this.sanitizer.bypassSecurityTrustResourceUrl('');

		this.locales = this.staticService.get('locales').map((locale) => {
			return {
				value: locale.id,
				label: locale.label
			};
		});

		this.languageName = this.getLanguageName();
		this.stateBadge = this.getStateBadge(this.dialogData.state);
		this.stateLabel = this.getStateLabel(this.dialogData.state);
		this.termName = this.dialogData.terms['name'] ? this.dialogData.terms['name'] : this.dialogData.terms['displayName'];
	}

	public ngAfterViewInit(): void {
		setTimeout(() => {
			this.adjustContentSize();
			setTimeout(() => {
				this.safeData = this.sanitizer.bypassSecurityTrustResourceUrl(this.dialogData.data);
			});
		});
		window.addEventListener('resize', this.adjustContentSize.bind(this));
	}

	public ngOnDestroy(): void {
		window.removeEventListener('resize', this.adjustContentSize);
	}

	public download(): void {
		if (this.dialogData?.download) {
			this.dialogData.download();
		}
	}

	public cancel(): void {
		if (this.dialogData?.callback) {
			this.dialogData.callback(false);
		}
	}

	public accept(): void {
		if (this.dialogData?.callback) {
			this.dialogData.callback(true);
		}
	}

	private getLanguageName(): string {
		if (isITerm(this.dialogData.terms)) {
			const locale = this.dialogData.terms?.locale;
			const localeObj: any = this.locales.filter((l) => l.value === locale);
			return localeObj && localeObj.length > 0 ? localeObj[0].label : locale;
		} else {
			return '';
		}
	}

	private adjustContentSize(): void {
		const padding = 24;
		const dialog = document.querySelector(`#${this.dialogRef.id}`)?.parentNode;

		if (!dialog) return;

		const dialogHeight = dialog['offsetHeight'] - padding * 2;
		const headerHeight = dialog.querySelector('h1')['offsetHeight'];
		const docNameHeight = dialog.querySelector('.document-name')?.['offsetHeight'];
		const actionsHeight = dialog.querySelector('div[mat-dialog-actions]')['offsetHeight'];
		const contentHeight = dialogHeight - headerHeight - actionsHeight;

		dialog.querySelector('.main-content')['style']['height'] = `${contentHeight}px`;
		dialog.querySelector('.legal-wrap')['style']['height'] = `${contentHeight - docNameHeight - padding}px`;
	}

	private getStateBadge(state: TermState): string {
		switch (state) {
			case TermState.live:
			case TermState.signed:
				return 'badge-success';
			case TermState.draft:
				return 'badge-warning';
			case TermState.unsigned:
				return 'badge-danger';
			default:
				return 'badge-dark';
		}
	}

	private getStateLabel(state: TermState): string {
		switch (state) {
			case TermState.live:
				return this.i18nService.get('active', 'app.descriptor.active');
			case TermState.draft:
				return this.i18nService.get('draft', 'app.descriptor.draft');
			case TermState.signed:
				return this.i18nService.get('accepted', 'app.descriptor.accepted-version');
			case TermState.unsigned:
				return this.i18nService.get('unsigned', 'app.descriptor.unsigned-version');
			default:
				return this.i18nService.get('archived', 'app.descriptor.archived');
		}
	}
}
